<template>
  <div class="available-date-picker">
    <!-- 自己渲染完整的 input-group（含左側圖示），不要塞進 base-input 的插槽，
         多包一層 div 會破壞 input-group 的橫向排列。 -->
    <div ref="field" class="input-group" :class="{focused: open}">
      <div class="input-group-prepend">
        <span class="input-group-text"><i class="fa fa-calendar-o"/></span>
      </div>
      <input
          type="text"
          class="form-control adp-input"
          readonly
          :placeholder="resolvedPlaceholder"
          :value="value"
          @click="toggle"
      >
    </div>

    <!-- 用 position: fixed + 自行計算座標，而不是相對父層的 absolute：
         頁面外層容器有 overflow: hidden，absolute 定位的彈窗會被裁掉一半，
         fixed 定位不受祖先 overflow 影響。 -->
    <div v-if="open" class="adp-panel" :style="panelStyle">
      <!-- 日期檢視：點標題可切換到月份檢視，快速跳月／跳年 -->
      <template v-if="mode === 'day'">
        <div class="adp-header">
          <button type="button" class="adp-nav" :disabled="!canGoPrev" @click="goPrev">
            <i class="fa fa-angle-left"/>
          </button>
          <button type="button" class="adp-title adp-title--clickable" @click="mode = 'month'">
            {{ viewYear }}年{{ viewMonth + 1 }}月
            <i class="fa fa-caret-down adp-title-caret"/>
          </button>
          <button type="button" class="adp-nav" :disabled="!canGoNext" @click="goNext">
            <i class="fa fa-angle-right"/>
          </button>
        </div>

        <div class="adp-weekdays">
          <span v-for="w in weekdayLabels" :key="w">{{ w }}</span>
        </div>

        <div class="adp-days">
          <button
              v-for="(cell, index) in dayCells"
              :key="index"
              type="button"
              class="adp-day"
              :class="{
                'adp-day--blank': !cell.date,
                'adp-day--selectable': cell.selectable,
                'adp-day--selected': cell.selected,
              }"
              :disabled="!cell.selectable"
              @click="pick(cell)"
          >{{ cell.day }}</button>
        </div>
      </template>

      <!-- 月份檢視：左右箭頭改成換年，沒有資料的年份／月份一樣不能選。
           precision='month' 時這就是唯一的檢視，標題不能再切回日期檢視。 -->
      <template v-else>
        <div class="adp-header">
          <button type="button" class="adp-nav" :disabled="!canGoPrevYear" @click="goPrevYear">
            <i class="fa fa-angle-left"/>
          </button>
          <button
              type="button"
              class="adp-title"
              :class="{'adp-title--clickable': precision === 'day'}"
              :disabled="precision !== 'day'"
              @click="mode = 'day'"
          >{{ viewYear }}年</button>
          <button type="button" class="adp-nav" :disabled="!canGoNextYear" @click="goNextYear">
            <i class="fa fa-angle-right"/>
          </button>
        </div>

        <div class="adp-months">
          <button
              v-for="cell in monthCells"
              :key="cell.month"
              type="button"
              class="adp-month"
              :class="{
                'adp-month--selectable': cell.selectable,
                'adp-month--current': cell.current,
              }"
              :disabled="!cell.selectable"
              @click="pickMonth(cell)"
          >{{ cell.month + 1 }}月</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// 只允許選「真的有資料」的日期用的日期選擇器。
// 瀏覽器原生 <input type="date"> 的月曆是瀏覽器自己畫的、無法個別停用某幾天，
// 而第三方套件（flatpickr）的樣式又跟專案的 Argon 主題互相打架，
// 所以這裡自己做一個，行為與外觀都完全可控。
const pad2 = (n) => String(n).padStart(2, '0');
const toKey = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

export default {
  name: 'AvailableDatePicker',
  props: {
    // v-model 值。precision='day' 時格式為 'YYYY-MM-DD'，'month' 時為 'YYYY-MM'
    value: {
      type: String,
      default: '',
    },
    // 可以被選取的清單，格式同 value；不在清單內的日期／月份會被停用
    availableDates: {
      type: Array,
      default: () => [],
    },
    // 'day' = 選到日，'month' = 只選到月（帳單頁用）
    precision: {
      type: String,
      default: 'day',
      validator: v => ['day', 'month'].includes(v),
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    const base = this.value || new Date().toISOString().slice(0, 7);
    const [y, m] = base.split('-').map(Number);
    return {
      open: false,
      // 'day' = 日期檢視，'month' = 月份檢視（點標題切換，用來快速跳月／跳年）
      mode: this.precision === 'month' ? 'month' : 'day',
      viewYear: y,
      viewMonth: m - 1,
      weekdayLabels: ['日', '一', '二', '三', '四', '五', '六'],
      panelStyle: {},
    };
  },
  computed: {
    availableSet() {
      return new Set(this.availableDates);
    },
    // 有可選日期的「年-月」集合，用來判斷月份檢視裡哪些月份可以點
    availableMonthSet() {
      return new Set(this.availableDates.map(k => k.slice(0, 7)));
    },
    yearBounds() {
      if (this.availableDates.length === 0) return null;
      const years = this.availableDates.map(k => Number(k.slice(0, 4)));
      return {min: Math.min(...years), max: Math.max(...years)};
    },
    canGoPrevYear() {
      return this.yearBounds === null || this.viewYear > this.yearBounds.min;
    },
    canGoNextYear() {
      return this.yearBounds === null || this.viewYear < this.yearBounds.max;
    },
    resolvedPlaceholder() {
      if (this.placeholder) return this.placeholder;
      return this.precision === 'month' ? '年/月' : '年/月/日';
    },
    // 目前選到的年、月（來自 value）；翻年份時用來判斷哪一格要標成選取狀態，
    // 不能只看 viewMonth，否則翻到別的年份還是會highlight同一個月份。
    selected() {
      if (!this.value) return null;
      const [y, m] = this.value.split('-').map(Number);
      return {year: y, month: m - 1};
    },
    monthCells() {
      return Array.from({length: 12}, (_, month) => ({
        month,
        selectable: this.availableMonthSet.has(`${this.viewYear}-${pad2(month + 1)}`),
        current: this.selected !== null
            && this.selected.year === this.viewYear
            && this.selected.month === month,
      }));
    },
    // 可選日期清單本身就界定了能翻到的範圍，超出範圍再翻也只會看到整片停用的月份
    monthBounds() {
      if (this.availableDates.length === 0) return null;
      const sorted = this.availableDates.slice().sort();
      const toMonthIndex = (key) => {
        const [y, m] = key.split('-').map(Number);
        return y * 12 + (m - 1);
      };
      return {
        min: toMonthIndex(sorted[0]),
        max: toMonthIndex(sorted[sorted.length - 1]),
      };
    },
    currentMonthIndex() {
      return this.viewYear * 12 + this.viewMonth;
    },
    canGoPrev() {
      return this.monthBounds === null || this.currentMonthIndex > this.monthBounds.min;
    },
    canGoNext() {
      return this.monthBounds === null || this.currentMonthIndex < this.monthBounds.max;
    },
    dayCells() {
      const firstWeekday = new Date(this.viewYear, this.viewMonth, 1).getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const cells = [];
      // 月初之前補空白格，讓 1 號對齊正確的星期欄位
      for (let i = 0; i < firstWeekday; i++) {
        cells.push({date: null, day: '', selectable: false, selected: false});
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const key = toKey(this.viewYear, this.viewMonth, d);
        cells.push({
          date: key,
          day: d,
          selectable: this.availableSet.has(key),
          selected: key === this.value,
        });
      }
      return cells;
    },
  },
  watch: {
    // 外部改變選取日期（例如切換發布日／截止日後重設預設值）時，畫面跟著跳到那個月份
    value(v) {
      if (!v) return;
      const [y, m] = v.split('-').map(Number);
      this.viewYear = y;
      this.viewMonth = m - 1;
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    // 彈窗是 fixed 定位、座標在打開當下算好，頁面捲動或改變大小後座標就不對了，
    // 與其持續重算，直接關掉比較單純（使用者再點一次即可）。
    window.addEventListener('scroll', this.close, true);
    window.addEventListener('resize', this.close);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('scroll', this.close, true);
    window.removeEventListener('resize', this.close);
  },
  methods: {
    onDocumentClick(e) {
      if (!this.$el.contains(e.target)) this.open = false;
    },
    close() {
      this.open = false;
    },
    toggle() {
      if (this.open) {
        this.open = false;
        return;
      }
      const rect = this.$refs.field.getBoundingClientRect();
      const PANEL_WIDTH = 280;
      // 靠近視窗右緣時往左對齊，避免彈窗超出畫面
      const left = Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8);
      this.panelStyle = {
        top: `${rect.bottom + 4}px`,
        left: `${Math.max(8, left)}px`,
      };
      // 每次重新打開都回到預設檢視，不要停在上次離開時的檢視
      this.mode = this.precision === 'month' ? 'month' : 'day';
      this.open = true;
    },
    goPrevYear() {
      if (this.canGoPrevYear) this.viewYear -= 1;
    },
    goNextYear() {
      if (this.canGoNextYear) this.viewYear += 1;
    },
    pickMonth(cell) {
      if (!cell.selectable) return;
      this.viewMonth = cell.month;
      // 只選到月份時，點下去就等於選好了，直接送出並關閉；
      // 選到日的模式則是回到日期檢視繼續挑哪一天。
      if (this.precision === 'month') {
        this.open = false;
        const key = `${this.viewYear}-${pad2(cell.month + 1)}`;
        if (key === this.value) return;
        this.$emit('input', key);
        this.$emit('change', key);
        return;
      }
      this.mode = 'day';
    },
    goPrev() {
      if (!this.canGoPrev) return;
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear -= 1;
      } else {
        this.viewMonth -= 1;
      }
    },
    goNext() {
      if (!this.canGoNext) return;
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear += 1;
      } else {
        this.viewMonth += 1;
      }
    },
    pick(cell) {
      if (!cell.selectable) return;
      this.open = false;
      if (cell.date === this.value) return;
      this.$emit('input', cell.date);
      this.$emit('change', cell.date);
    },
  },
};
</script>

<style scoped>
.available-date-picker {
  position: relative;
}

/* 輸入框設 readonly 是為了讓使用者只能從月曆挑日期（不能亂打字），
   但 Bootstrap 的 .form-control[readonly] 會套灰底，看起來像停用的欄位，
   這裡蓋回白底；選擇器同樣帶 [readonly] 才壓得過 Bootstrap 的權重。 */
.adp-input[readonly] {
  cursor: pointer;
  background-color: #fff;
}

.adp-panel {
  position: fixed;
  z-index: 1050;
  width: 280px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.adp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.adp-title {
  font-size: 14px;
  font-weight: 600;
  color: #32325d;
  padding: 4px 8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
}

.adp-title--clickable {
  cursor: pointer;
}

.adp-title--clickable:hover {
  background: #f6f9fc;
}

.adp-title-caret {
  margin-left: 4px;
  font-size: 12px;
  color: #8898aa;
}

.adp-nav {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #8898aa;
  cursor: pointer;
}

.adp-nav:hover:not(:disabled) {
  background: #f6f9fc;
  color: #32325d;
}

.adp-nav:disabled {
  color: #dee2e6;
  cursor: default;
}

.adp-weekdays,
.adp-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.adp-weekdays span {
  padding: 4px 0;
  text-align: center;
  font-size: 12px;
  color: #8898aa;
}

.adp-day {
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 13px;
  color: #ced4da;
  cursor: default;
}

.adp-day--blank {
  visibility: hidden;
}

.adp-day--selectable {
  color: #32325d;
  cursor: pointer;
}

.adp-day--selectable:hover {
  background: #f6f9fc;
}

.adp-day--selected,
.adp-day--selected:hover {
  background: #F39800;
  color: #fff;
  font-weight: 600;
}

/* 月份檢視：3 欄 × 4 列，高度跟日期檢視相近，切換時版面不會跳動 */
.adp-months {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px 0;
}

.adp-month {
  height: 46px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  color: #ced4da;
  cursor: default;
}

.adp-month--selectable {
  color: #32325d;
  cursor: pointer;
}

.adp-month--selectable:hover {
  background: #f6f9fc;
}

.adp-month--current.adp-month--selectable {
  background: #F39800;
  color: #fff;
  font-weight: 600;
}
</style>
